import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useToast } from '@vnetwork/ui/feedback';
import { useOutputStorageConfig } from '@/api/media/shared-storage';
import { useCDNs } from '@/api/media/shared-cdns';
import { useVodPresetGroups } from '@/api/media/vod-presets';
import { useCreateVodJob } from '@/api/media/vod-jobs';
import { useNotificationCallbacks } from '@/api/media/vod-notifications';
import { vodFileTransferConfig, vodCDNConfig } from '@/api/media/configs';
import type { VodDistributionConfig, VodProtectionConfig } from '@/api/media/vod-jobs-types';

// ============================================
// Types
// ============================================

export type SourceProtocol = 'ftp' | 'http' | 'sftp' | 'webdav' | 's3';
export type AdditionalTrackType = 'audio' | 'subtitle';
export type OutputKey = 'hls' | 'dash' | 'mss' | 'mp4';
export type ProtectionKey = 'none' | 'aes' | 'verimatrix';
export type HlsProtectionGroup = 'none' | 'aes' | 'verimatrix';

export interface AdditionalTrack {
  type: AdditionalTrackType;
  language: string;
  file_path: string;
}

export interface OutputDistributionConfig {
  target: string;
  storage: string;
  path: string;
  encryptType: string;
}

export interface JobFormData {
  source: {
    path: string;
    protocol: SourceProtocol;
    storage: string;
    additionalTracks: AdditionalTrack[];
  };
  presets: string[];
  outputOptions: Record<OutputKey, {
    enabled: boolean;
    twoPass: boolean;
    blackFrameRemoval: boolean;
    protection: ProtectionKey;
    noneEnabled?: boolean;
    aesEnabled?: boolean;
    aesKeyRotation?: boolean;
    aesResourceId?: string;
    verimatrixEnabled?: boolean;
    verimatrixKeyRotation?: boolean;
    verimatrixResourceId?: string;
    verimatrixId?: string;
    distributionConfigs?: OutputDistributionConfig[];
    draftDistribution?: OutputDistributionConfig;
    distributionGroups?: Record<HlsProtectionGroup, OutputDistributionConfig[]>;
    draftDistributionGroups?: Record<HlsProtectionGroup, OutputDistributionConfig>;
    multiplex?: boolean;
    fragmented?: boolean;
  }>;
  globalOverrides: {
    transmuxOnly: boolean;
    audioOnly: boolean;
    thumbnailSeeking: boolean;
    gpuTranscoding: boolean;
  };
  md5sum: string;
  callback_id?: number;
}

// ============================================
// Enum maps (must match legacy jobUtils.ts exactly)
// ============================================

/** Legacy OUTPUT_FORMAT_MAP — numeric string codes per API contract */
export const OUTPUT_FORMAT_MAP: Record<OutputKey, string> = {
  hls: '1',
  dash: '2',
  mss: '3',
  mp4: '6',
};

/** Legacy INGESTION_TYPE_MAP — numeric string codes per API contract */
export const INGESTION_TYPE_MAP: Record<string, string> = {
  ftp: '1',
  http: '2',
  sftp: '5',
  webdav: '6',
  s3: '8',
};

/** Legacy DISTRIBUTION_TYPE_MAP — numeric string codes per API contract */
export const DISTRIBUTION_TYPE_MAP: Record<string, string> = {
  cdn: '3',
  ftp: '1',
  sftp: '4',
  azure: '7',
  s3: '8',
};

// ============================================
// Constants
// ============================================

const PROTOCOL_KEY_MAP: Record<string, keyof import('@/api/media/shared-storage-types').OutputStorageConfig> = {
  ftp: 'ftp',
  http: 'https',
  sftp: 'sftp',
  webdav: 'webdav',
  s3: 's3',
};

/** Maps distribution target → output config key. cdn handled separately via useCDNs. */
const TARGET_KEY_MAP: Record<string, keyof import('@/api/media/shared-storage-types').OutputStorageConfig> = {
  ftp: 'ftp',
  sftp: 'sftp',
  azure: 'https',
  s3: 's3',
  webdav: 'webdav',
};

const INITIAL_FORM_DATA: JobFormData = {
  source: { path: '', protocol: 'ftp', storage: '', additionalTracks: [] },
  presets: [],
  outputOptions: {
    hls: {
      enabled: true, twoPass: false, multiplex: false, blackFrameRemoval: false,
      protection: 'none', noneEnabled: true, aesEnabled: false, aesKeyRotation: false,
      aesResourceId: '', verimatrixEnabled: false, verimatrixKeyRotation: false,
      verimatrixResourceId: '', verimatrixId: '',
      distributionConfigs: [],
      draftDistribution: { target: 'cdn', storage: '', path: '', encryptType: '3' },
      distributionGroups: { none: [], aes: [], verimatrix: [] },
      draftDistributionGroups: {
        none: { target: 'cdn', storage: '', path: '', encryptType: '3' },
        aes: { target: 'cdn', storage: '', path: '', encryptType: '2' },
        verimatrix: { target: 'cdn', storage: '', path: '', encryptType: '1' },
      },
    },
    dash: {
      enabled: false, twoPass: false, blackFrameRemoval: false, protection: 'none',
      distributionConfigs: [],
      draftDistribution: { target: 'cdn', storage: '', path: '', encryptType: '3' },
    },
    mss: {
      enabled: false, twoPass: false, blackFrameRemoval: false, protection: 'none',
      distributionConfigs: [],
      draftDistribution: { target: 'cdn', storage: '', path: '', encryptType: '3' },
    },
    mp4: {
      enabled: false, twoPass: false, fragmented: false, blackFrameRemoval: false, protection: 'none',
      distributionConfigs: [],
      draftDistribution: { target: 'cdn', storage: '', path: '', encryptType: '3' },
    },
  },
  globalOverrides: { transmuxOnly: false, audioOnly: false, thumbnailSeeking: false, gpuTranscoding: false },
  md5sum: '',
};

// ============================================
// Helpers
// ============================================

export function getStorageLabel(storage: Record<string, unknown>, protocol: string): string {
  const proto = protocol.toLowerCase();
  if (proto === 'ftp' || proto === 'sftp') {
    return `${proto}://${storage?.username ?? storage?.name ?? 'storage'}:***@${storage?.host ?? 'host'}:${storage?.port ?? 21}`;
  }
  if (proto === 's3') {
    return `s3://${storage?.access_key_id ?? storage?.name ?? 'storage'}:***:${storage?.region ?? 'us-east-1'}:${storage?.bucket ?? 'bucket'}`;
  }
  if (proto === 'webdav') {
    return `webdav://${storage?.username ?? storage?.name ?? 'storage'}:***@${storage?.host ?? 'host'}`;
  }
  return `${proto}://${storage?.host ?? storage?.endpoint ?? storage?.name ?? 'storage'}`;
}

/** Legacy getSourceType: http|webdav→2, else 1 */
function getSourceType(protocol: string): number {
  return protocol === 'http' || protocol === 'webdav' ? 2 : 1;
}

/** Legacy INGESTION_TYPE_MAP lookup — returns numeric string code */
function getIngestionType(protocol: string): string {
  return INGESTION_TYPE_MAP[protocol.toLowerCase()] ?? '1';
}

/** Legacy DISTRIBUTION_TYPE_MAP lookup — returns numeric string code */
function getDistributionType(target: string): string {
  return DISTRIBUTION_TYPE_MAP[target.toLowerCase()] ?? '3';
}

// ============================================
// Payload builder (mirrors legacy useJobForm.submit.ts exactly)
// ============================================

function buildPayload(form: JobFormData, selectedFormats: OutputKey[]) {
  const transcodingConfigs = selectedFormats.map((fmt) => {
    const cfg = form.outputOptions[fmt] as Record<string, unknown>;

    // ── Protection configs (legacy submit.ts:100-131) ──
    let protectionConfigs: VodProtectionConfig[];
    if (fmt === 'hls') {
      const hlsProts: VodProtectionConfig[] = [];
      if (cfg.noneEnabled) {
        hlsProts.push({ key_type: 1, type: '3' });
      }
      if (cfg.aesEnabled) {
        hlsProts.push({
          key_type: cfg.aesKeyRotation ? 2 : 1,
          type: 2,
          resource_id: String(cfg.aesResourceId ?? ''),
        });
      }
      if (cfg.verimatrixEnabled) {
        hlsProts.push({
          key_type: cfg.verimatrixKeyRotation ? 2 : 1,
          resource_id: String(cfg.verimatrixResourceId ?? ''),
          verimatrix_id: String(cfg.verimatrixId ?? ''),
          type: 1,
        });
      }
      protectionConfigs = hlsProts;
    } else {
      protectionConfigs = [{ key_type: 1, type: '3' }];
    }

    // ── Distribution configs (legacy submit.ts:140-173) ──
    let distributionConfigs: VodDistributionConfig[];
    if (fmt === 'hls') {
      const groups: HlsProtectionGroup[] = ['none', 'aes', 'verimatrix'];
      distributionConfigs = groups.flatMap((group) => {
        if (group === 'none' && !cfg.noneEnabled) return [];
        if (group === 'aes' && !cfg.aesEnabled) return [];
        if (group === 'verimatrix' && !cfg.verimatrixEnabled) return [];
        const drafts = cfg.draftDistributionGroups as Record<string, OutputDistributionConfig> | undefined;
        const dist = drafts?.[group];
        if (!dist) return [];
        return [{
          distribution_type: getDistributionType(dist.target),
          distribution_id: dist.storage,
          path: dist.path,
          encrypt_type: group === 'none' ? '3' : group === 'aes' ? '2' : '1',
        }];
      });
    } else {
      const dist = cfg.draftDistribution as OutputDistributionConfig | undefined;
      distributionConfigs = dist ? [{
        distribution_type: getDistributionType(dist.target),
        distribution_id: dist.storage,
        path: dist.path,
        encrypt_type: '3',
      }] : [];
    }

    return {
      format: OUTPUT_FORMAT_MAP[fmt],
      mix_audio_video: Boolean(cfg.multiplex),
      pass: cfg.twoPass ? 2 : 1,
      // fragment_mp4 only for mp4 (legacy submit.ts:137)
      ...(fmt === 'mp4' ? { fragment_mp4: Boolean(cfg.fragmented) } : {}),
      has_thumbnail: Boolean(form.globalOverrides.thumbnailSeeking),
      black_frame_removal: Boolean(cfg.blackFrameRemoval),
      distribution_configs: distributionConfigs,
      protection_configs: protectionConfigs,
    };
  });

  return {
    file_name: form.source.path.split('/').slice(-1)[0] || form.source.path,
    spec: {
      source_type: getSourceType(form.source.protocol),
      ingestion_config: {
        ingestion_type: getIngestionType(form.source.protocol),
        ingestion_id: form.source.storage,
        file_path: form.source.path,
        file_md5: form.md5sum,
        // Additional tracks (legacy submit.ts:186-198)
        additional_audios: form.source.additionalTracks
          .filter((t) => t.type === 'audio')
          .map((t) => ({ language: t.language, file_path: t.file_path })),
        additional_subtitles: form.source.additionalTracks
          .filter((t) => t.type === 'subtitle')
          .map((t) => ({ language: t.language, file_path: t.file_path })),
      },
      transcoding_configs: transcodingConfigs,
      use_gpu: form.globalOverrides.gpuTranscoding,
      // thumbnail_seeking conditional (legacy submit.ts:202)
      ...(form.globalOverrides.thumbnailSeeking ? { thumbnail_seeking: true } : {}),
      preset_configs: form.presets.map((id) => ({ preset_id: id })),
      transmux_only: form.globalOverrides.transmuxOnly,
      audio_only: form.globalOverrides.audioOnly,
      callback_id: form.callback_id,
    },
  };
}

// ============================================
// Validation (mirrors legacy submit.ts:23-90)
// ============================================

function validateForm(form: JobFormData, selectedFormats: OutputKey[]): string | null {
  if (!form.source.protocol) return 'Source protocol is required.';
  if (!form.source.storage) return 'Source storage is required.';
  if (!form.source.path.trim()) return 'Input file path is required.';

  // audioOnly preset exemption (legacy: audioOnly skips preset check)
  if (!form.globalOverrides.audioOnly && form.presets.length === 0) {
    return 'At least one preset is required.';
  }
  if (selectedFormats.length === 0) {
    return 'At least one output format must be selected.';
  }
  // audio-only requires HLS
  if (form.globalOverrides.audioOnly && !selectedFormats.includes('hls')) {
    return 'Audio only mode requires HLS output.';
  }

  // Per-format distribution required
  for (const fmt of selectedFormats) {
    const cfg = form.outputOptions[fmt] as Record<string, unknown>;
    if (fmt === 'hls') {
      const drafts = cfg.draftDistributionGroups as Record<string, OutputDistributionConfig> | undefined;
      const missingGroup =
        (cfg.noneEnabled && (!drafts?.none?.target || !drafts?.none?.storage || !drafts?.none?.path?.trim())) ||
        (cfg.aesEnabled && (!drafts?.aes?.target || !drafts?.aes?.storage || !drafts?.aes?.path?.trim())) ||
        (cfg.verimatrixEnabled && (!drafts?.verimatrix?.target || !drafts?.verimatrix?.storage || !drafts?.verimatrix?.path?.trim()));
      if (missingGroup) {
        return 'Each selected HLS protection group must have a complete distribution configuration.';
      }
    } else {
      const dist = cfg.draftDistribution as OutputDistributionConfig | undefined;
      if (!dist?.target || !dist?.storage || !dist?.path?.trim()) {
        return 'Each selected output format must have at least one distribution configuration.';
      }
    }
  }

  // AES / Verimatrix resource-id required
  if (selectedFormats.includes('hls')) {
    const hlsCfg = form.outputOptions.hls as Record<string, unknown>;
    if (hlsCfg.aesEnabled && !String(hlsCfg.aesResourceId ?? '').trim()) {
      return 'AES Resource ID is required for HLS protection.';
    }
    if (hlsCfg.verimatrixEnabled && !String(hlsCfg.verimatrixResourceId ?? '').trim()) {
      return 'Verimatrix Resource ID is required for HLS protection.';
    }
    if (hlsCfg.verimatrixEnabled && !String(hlsCfg.verimatrixId ?? '').trim()) {
      return 'Please select a Verimatrix DRM endpoint for HLS protection.';
    }
  }

  return null;
}

// ============================================
// Hook
// ============================================

export function useJobCreateForm(serviceId: string) {
  const navigate = useNavigate();
  const toast = useToast();

  const { data: outputs, isLoading: outputsLoading } = useOutputStorageConfig(
    serviceId,
    vodFileTransferConfig,
  );
  const { data: cdns = [], isLoading: cdnsLoading } = useCDNs(
    serviceId,
    vodCDNConfig,
  );
  const { data: presets, isLoading: presetsLoading } = useVodPresetGroups(serviceId);
  const { data: notifications = [], isLoading: notifLoading } = useNotificationCallbacks(serviceId);
  const createJob = useCreateVodJob(serviceId);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [form, setForm] = useState<JobFormData>(INITIAL_FORM_DATA);
  const [formError, setFormError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<{ sourcePath?: string; presets?: string; outputs?: string }>({});

  // Auto-select first available source storage when protocol changes
  const currentStorages = (() => {
    const key = PROTOCOL_KEY_MAP[form.source.protocol];
    if (!key || !outputs) return [];
    const list = (outputs as unknown as Record<string, unknown[]>)[key];
    return Array.isArray(list) ? list : [];
  })() as Record<string, unknown>[];

  useEffect(() => {
    if (!currentStorages.length) return;
    setForm((prev) => {
      const still = currentStorages.some(
        (s) => String(s?.id ?? s?.name ?? '') === prev.source.storage,
      );
      if (still && prev.source.storage) return prev;
      return {
        ...prev,
        source: { ...prev.source, storage: String(currentStorages[0]?.id ?? currentStorages[0]?.name ?? '') },
      };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.source.protocol, outputs]);

  // Auto-populate CDN storage in draft distribution groups when cdns load
  useEffect(() => {
    if (!cdns.length) return;
    setForm((prev) => {
      const firstCdnId = String(cdns[0]?.id ?? cdns[0]?.name ?? '');
      let changed = false;
      const nextOptions = { ...prev.outputOptions };

      (Object.keys(nextOptions) as OutputKey[]).forEach((key) => {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const opt = nextOptions[key] as any;

        // Non-HLS: update draftDistribution if target is cdn and storage empty
        if (opt.draftDistribution?.target === 'cdn' && !opt.draftDistribution?.storage) {
          nextOptions[key] = { ...opt, draftDistribution: { ...opt.draftDistribution, storage: firstCdnId } };
          changed = true;
        }

        // HLS: update each group draft
        if (opt.draftDistributionGroups) {
          const groups = { ...opt.draftDistributionGroups };
          let groupChanged = false;
          (['none', 'aes', 'verimatrix'] as HlsProtectionGroup[]).forEach((g) => {
            if (groups[g]?.target === 'cdn' && !groups[g]?.storage) {
              groups[g] = { ...groups[g], storage: firstCdnId };
              groupChanged = true;
            }
          });
          if (groupChanged) {
            nextOptions[key] = { ...opt, draftDistributionGroups: groups };
            changed = true;
          }
        }
      });

      return changed ? { ...prev, outputOptions: nextOptions } : prev;
    });
  }, [cdns]);

  /** Returns storage list for a given distribution target. cdn target returns CDN list. */
  const getStoragesByTarget = (target: string): Record<string, unknown>[] => {
    if (target.toLowerCase() === 'cdn') return cdns as unknown as Record<string, unknown>[];
    const key = TARGET_KEY_MAP[target.toLowerCase()];
    if (!key || !outputs) return [];
    const list = (outputs as unknown as Record<string, unknown[]>)[key];
    return Array.isArray(list) ? (list as Record<string, unknown>[]) : [];
  };

  const selectedStorage = currentStorages.find(
    (s) => String(s?.id ?? s?.name ?? '') === form.source.storage,
  );

  // ---- Mutators ----

  const clearFormError = () => { setFormError(null); };

  const updateSource = (field: string, value: string) => {
    setForm((p) => ({ ...p, source: { ...p.source, [field]: value } }));
    if (field === 'path') setFieldErrors((p) => ({ ...p, sourcePath: undefined }));
    clearFormError();
  };

  const updateMd5sum = (v: string) => setForm((p) => ({ ...p, md5sum: v }));

  const addAdditionalTrack = (track: AdditionalTrack) =>
    setForm((p) => ({ ...p, source: { ...p.source, additionalTracks: [...p.source.additionalTracks, track] } }));

  const removeAdditionalTrack = (index: number) =>
    setForm((p) => ({
      ...p,
      source: { ...p.source, additionalTracks: p.source.additionalTracks.filter((_, i) => i !== index) },
    }));

  const togglePreset = (value: string) => {
    setForm((p) => ({
      ...p,
      presets: p.presets.includes(value) ? p.presets.filter((k) => k !== value) : [...p.presets, value],
    }));
    setFieldErrors((p) => ({ ...p, presets: undefined }));
    clearFormError();
  };

  /** Selection is driven exclusively by the `enabled` flag — mirrors legacy Boolean(cfg.enabled). */
  const isOutputSelected = (key: OutputKey) => Boolean(form.outputOptions[key].enabled);

  /** Toggle card on/off: only flips `enabled`. Never mutates twoPass/multiplex/fragmented/blackFrameRemoval. */
  const toggleOutputCard = (key: OutputKey) => {
    setForm((p) => ({
      ...p,
      outputOptions: {
        ...p.outputOptions,
        [key]: { ...p.outputOptions[key], enabled: !p.outputOptions[key].enabled },
      },
    }));
    setFieldErrors((p) => ({ ...p, outputs: undefined }));
    clearFormError();
  };

  const toggleCardOption = (key: OutputKey, field: string) =>
    setForm((p) => ({
      ...p,
      outputOptions: {
        ...p.outputOptions,
        [key]: {
          ...(p.outputOptions[key] as Record<string, unknown>),
          enabled: true,
          [field]: !(p.outputOptions[key] as Record<string, unknown>)[field],
        },
      },
    }));

  const updateOutputOption = (key: OutputKey, field: string, value: unknown) =>
    setForm((p) => ({
      ...p,
      outputOptions: {
        ...p.outputOptions,
        [key]: { ...(p.outputOptions[key] as Record<string, unknown>), enabled: true, [field]: value },
      },
    }));

  const updateGlobalOverride = (field: string, value: boolean) =>
    setForm((p) => ({ ...p, globalOverrides: { ...p.globalOverrides, [field]: value } }));

  const updateDistributionDraft = (key: OutputKey, field: keyof OutputDistributionConfig, value: string) =>
    setForm((p) => ({
      ...p,
      outputOptions: {
        ...p.outputOptions,
        [key]: {
          ...(p.outputOptions[key] as Record<string, unknown>),
          draftDistribution: {
            ...((p.outputOptions[key] as Record<string, unknown>).draftDistribution as OutputDistributionConfig ?? {}),
            [field]: value,
            // reset storage when target changes
            ...(field === 'target' ? { storage: '' } : {}),
          },
        },
      },
    }));

  const updateDistributionGroupDraft = (
    key: OutputKey,
    group: HlsProtectionGroup,
    field: keyof OutputDistributionConfig,
    value: string,
  ) =>
    setForm((p) => {
      const prev = p.outputOptions[key] as Record<string, unknown>;
      const groups = (prev.draftDistributionGroups as Record<string, OutputDistributionConfig>) ?? {};
      return {
        ...p,
        outputOptions: {
          ...p.outputOptions,
          [key]: {
            ...prev,
            draftDistributionGroups: {
              ...groups,
              [group]: {
                ...(groups[group] ?? {}),
                [field]: value,
                // reset storage when target changes
                ...(field === 'target' ? { storage: '' } : {}),
              },
            },
          },
        },
      };
    });

  const updateNotification = (value?: number) =>
    setForm((p) => ({ ...p, callback_id: value }));

  // ---- Submit ----

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const selectedFormats = (Object.keys(form.outputOptions) as OutputKey[]).filter(isOutputSelected);

    // Per-field validation (surfaced inline)
    const fe: { sourcePath?: string; presets?: string; outputs?: string } = {};
    if (!form.source.path.trim()) fe.sourcePath = 'Input file path is required.';
    if (!form.globalOverrides.audioOnly && form.presets.length === 0) fe.presets = 'Select at least one preset.';
    if (selectedFormats.length === 0) fe.outputs = 'Select at least one output format.';
    if (Object.keys(fe).length > 0) {
      setFieldErrors(fe);
      setFormError('Fix the errors below before submitting.');
      return;
    }

    // Full validation (distribution, DRM resource IDs, etc.)
    const validationError = validateForm(form, selectedFormats);
    if (validationError) {
      setFormError(validationError);
      return;
    }

    setFieldErrors({});
    setFormError(null);
    setIsSubmitting(true);
    try {
      await createJob.mutateAsync(buildPayload(form, selectedFormats));
      toast.success('Job created successfully');
      setTimeout(() => navigate(`/services/vod/${serviceId}/jobs`), 1500);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Failed to create job';
      toast.error(msg);
    } finally {
      setIsSubmitting(false);
    }
  };

  const canSubmit = !isSubmitting;

  return {
    form,
    currentStorages,
    selectedStorage,
    presets: presets ?? [],
    notifications,
    isPageLoading: outputsLoading || presetsLoading || notifLoading || cdnsLoading,
    isSubmitting,
    canSubmit,
    formError,
    fieldErrors,
    updateSource,
    updateMd5sum,
    addAdditionalTrack,
    removeAdditionalTrack,
    togglePreset,
    isOutputSelected,
    toggleOutputCard,
    toggleCardOption,
    updateOutputOption,
    updateGlobalOverride,
    updateDistributionDraft,
    updateDistributionGroupDraft,
    updateNotification,
    getStoragesByTarget,
    handleSubmit,
    navigate,
  };
}
